import { IFilterParams } from '@ag-grid-community/core';
import { FormHelperText, Grid, Typography } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { ActionButton } from '@tassat/button';
import { AgGridFilterType, SelectOption } from '@tassat/common-definitions';
import React from 'react';

import {
    TableOptionsContext,
    handleFilterChange,
    handleSortChange,
    isColumnFiltered,
    isColumnSorted
} from 'SharedComponents/contexts/TableOptionsContext';

import FilterInput from './filterInput';
import { ServerSort } from '../serverSort';

interface TextFilterProps extends WithStyles<typeof muiStyles>, IFilterParams {
    inputType: 'text' | 'textEquals' | 'number' | 'select' | 'clientCounterparty' | 'assetAmount';
    getMappedColumn?: (string) => string;
    getMappedTableColumn?: (string) => string;
    defaultSortField?: boolean;
    defaultSortDirection?: 'ASC' | 'DESC';
    selectOptions?: SelectOption[] | string;
    hideSort?: boolean;
    validators?: ((value: any) => string | null)[];
}

const muiStyles = () => ({
    root: { padding: '1rem' },
    button: { marginTop: '.5rem' },
    filterDisclaimer: {
        margin: '.25rem',
        fontSize: '.8rem',
        textAlign: 'center' as const
    }
});

const ExternalFilter = React.forwardRef((props: TextFilterProps, ref) => {
    const {
        classes,
        colDef,
        inputType,
        getMappedColumn,
        defaultSortField,
        defaultSortDirection,
        selectOptions,
        hideSort,
        validators,
        context,
        getMappedTableColumn
    } = props;

    const {
        TableOptions,
        TableOptionsDispatch
    } = React.useContext(TableOptionsContext);
    const [ operation, setOperation ] = React.useState<string>(inputType === 'select' ? AgGridFilterType.Equal : '');
    const [ filterValue, setFilterValue ] = React.useState<string>('');
    const [ filterInputError, setFilterInputError ] = React.useState<string>('');
    const [ filterDirection, setFilterDirection ] = React.useState<string>('');
    const [ filterAsset, setFilterAsset ] = React.useState<string>('');
    const [ sortDirection, setSortDirection ] = React.useState<'ASC' | 'DESC' | 'default' | false>(false);

    React.useEffect((): void => {
        if (validators) {
            for (const validator of validators) {
                const errorMessage = validator(filterValue);

                if (errorMessage) {
                    //info: handle one validation at a time.
                    setFilterInputError(errorMessage);
                    break;
                } else {
                    setFilterInputError('');
                }
            }
        }
    }, [filterValue]);

    const checkAgainstTable = () => {
        const tableSort = isColumnSorted(colDef.field, getMappedColumn, TableOptions);
        if (tableSort !== sortDirection) {
            setSortDirection(tableSort);

            return tableSort;
        }

        const tableFilter = isColumnFiltered(inputType === 'clientCounterparty' ?
            `${filterDirection}${colDef.field}`
            : colDef.field,
        getMappedColumn,
        TableOptions,
        getMappedTableColumn);

        if (filterValue && !tableFilter) {
            setFilterValue('');
            setFilterDirection('');
            setFilterAsset('');
            setOperation(inputType === 'select' ? AgGridFilterType.Equal : '');

            return tableFilter;
        }

        return filterValue || sortDirection;
    };

    const inputRef = React.useRef<any>();
    React.useImperativeHandle(ref, () => {
        return {

            isFilterActive() {
                const tableCheck = checkAgainstTable();

                return (tableCheck && tableCheck !== 'default' && filterValue && operation) || tableCheck === 'ASC' || tableCheck === 'DESC';
            },

            afterGuiAttached: () => {
                checkAgainstTable();
            },

            doesFilterPass: () => {
                // Always true since server is doing the filtering
                return true;
            }
        };
    });

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            ref={inputRef}
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                container
                direction='row'
                justifyContent='space-between'
                spacing={0}
                wrap='nowrap'
            >
                {!hideSort &&
                    (<ServerSort
                        defaultSortDirection={defaultSortDirection}
                        defaultSortField={defaultSortField}
                        field={colDef.field}
                        getMappedColumn={getMappedColumn}
                        handleSortChange={(
                        field, direction
                    ) => handleSortChange(field, direction, getMappedColumn, TableOptions, TableOptionsDispatch)}
                        id={`${colDef.field}_externalFilter`}
                        isColumnSorted={(field) => isColumnSorted(field, getMappedColumn, TableOptions)}
                        setSortDirectionProp={setSortDirection}
                        sortDirectionProp={sortDirection}
                    />
                )}
            </Grid>
            <FilterInput
                filterAsset={inputType === 'assetAmount' && filterAsset}
                filterDirection={inputType === 'clientCounterparty' && filterDirection}
                filterValue={filterValue}
                id={`${colDef.field}_externalFilter`}
                operation={operation}
                selectOptions={typeof selectOptions === 'string' ? context[selectOptions] : selectOptions}
                setFilterAsset={inputType === 'assetAmount' && setFilterAsset}
                setFilterDirection={inputType === 'clientCounterparty' && setFilterDirection}
                setFilterValue={setFilterValue}
                setOperation={setOperation}
                type={inputType}
            />
            {(inputType === 'text' || inputType === 'clientCounterparty') &&
                <Typography
                    className={classes.filterDisclaimer}
                    variant='body2'
                >Text filters are case sensitive
                </Typography>}
            {
                filterInputError &&
                <FormHelperText error>{filterInputError}</FormHelperText>
            }
            <ActionButton
                className={classes.button}
                disabled={!!filterInputError}
                handleClick={(e) => {
                    const value = (
                        inputType === 'text' ||
                        inputType === 'textEquals' ||
                        inputType === 'clientCounterparty' ||
                        inputType === 'assetAmount'
                    ) ? filterValue.trim()
                        : filterValue;
                    let field = colDef.field;
                    const queryValue = undefined;

                    if (inputType === 'clientCounterparty') {
                        if (!filterDirection) return;

                        field = `${filterDirection}${colDef.field}`;
                    }

                    // TODO Short term fix to avoid modifying existing logic
                    // if (inputType === 'assetAmount') {
                    //     if (!filterAsset) return;

                    //     queryValue = filterAsset === AssetDropdownValues.USD ? 'fiatAmount' : 'assetQuantity';
                    // }

                    handleFilterChange(
                        field,
                        operation,
                        value,
                        getMappedColumn,
                        TableOptions,
                        TableOptionsDispatch,
                        queryValue,
                        getMappedTableColumn
                    );
                }}
                id={`${colDef.field}_externalFilter_applyFilter`}
                text='Apply'
                type='primary'
            />
            <ActionButton
                className={classes.button}
                handleClick={(e) => {
                    setFilterValue('');
                    setOperation(inputType === 'select' ? AgGridFilterType.Equal : '');
                    setSortDirection('default');
                    let field = colDef.field;

                    if (inputType === 'clientCounterparty' && filterDirection) {
                        field = `${filterDirection}${colDef.field}`;
                    }

                    handleFilterChange(
                        field,
                        AgGridFilterType.ClearFilter,
                        null,
                        getMappedColumn,
                        TableOptions,
                        TableOptionsDispatch
                    );

                    if (handleSortChange) {
                        handleSortChange(
                            colDef.field,
                            AgGridFilterType.ClearFilter,
                            getMappedColumn,
                            TableOptions,
                            TableOptionsDispatch
                        );
                    }
                    if (inputType === 'clientCounterparty') {
                        setFilterDirection('');
                    }
                    if (inputType === 'assetAmount') {
                        setFilterAsset('');
                    }
                }}
                id={`${colDef.field}_externalFilter_clearFilter`}
                text='Clear'
                type='muted'
            />
        </Grid>
    );
});

ExternalFilter.displayName = 'ExternalFilter';

export default withStyles(muiStyles)(ExternalFilter);